import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXsrfConfiguration } from '@angular/common/http';
import { APP_INITIALIZER, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule, NoopAnimationsModule } from '@angular/platform-browser/animations';
import { StepUpInterceptor } from '@backbase/identity-auth/step-up';
import { NOTIFICATIONS_BADGE_NOTIFICATIONS_BASE_PATH } from '@backbase/notifications-ang';
import { RemoteConfigService } from '@backbase/remote-config-ang';
import localeGB from '@angular/common/locales/en-GB';
import { DatePipe, registerLocaleData } from '@angular/common';
import { ACCOUNTS_TRANSACTIONS_JOURNEY_ARRANGEMENT_MANAGER_BASE_PATH } from '@backbase/accounts-transactions-journey-ang';
import { environment } from '../environments/environment';
import { appModuleImports } from './app-module-imports';
import { AppComponent } from './app.component';
import { LoanInterceptor } from './interceptors/loan.interceptor';
import { RetailAppRemoteConfig } from './remote-config/remote-config';
import { APP_ARRANGEMENT_MANAGER_BASE_PATH, APP_NOTIFICATIONS_BASE_PATH, ServicePathsModule } from './service-paths.module';
import { TransfersInterceptor } from './interceptors/payments.interceptor';
import { AccountsInterceptor } from './interceptors/accounts.interceptor';
import { TransfersSaveBeneficiaryInterceptor } from './interceptors/payments-save-beneficiary.interceptor';
import {
  DEV_TOOLS_PREVENTION_ENABLED,
  DevToolsPreventionService,
} from './interceptors/dev-tools-prevention.service';
import { AMOUNT_CONFIG_TOKEN, AmountConfig } from '@backbase/ui-ang/amount';
import { ENVIRONMENT_CONFIG, EnvironmentConfig } from '@backbase/shared/feature/config';

registerLocaleData(localeGB, 'en-GB');

export function applicationInitializer(remoteConfig: RemoteConfigService<RetailAppRemoteConfig>) {
  return () => remoteConfig.fetchAndActivate();
}

export function devToolsPreventionInitializer(devToolsPreventionService: DevToolsPreventionService) {
  return () => devToolsPreventionService.initialize();
}

const amountConfig: AmountConfig = {
  mapCurrency: false,
};

const environmentConfig: EnvironmentConfig = {
  localStorageEncryptionKey: environment.localStorageEncryptionKey,
};

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    environment.animation ? BrowserAnimationsModule : NoopAnimationsModule,
    ServicePathsModule,
    ...appModuleImports
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: applicationInitializer,
      multi: true,
      deps: [RemoteConfigService],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: devToolsPreventionInitializer,
      multi: true,
      deps: [DevToolsPreventionService],
    },
    {
      provide: NOTIFICATIONS_BADGE_NOTIFICATIONS_BASE_PATH,
      useExisting: APP_NOTIFICATIONS_BASE_PATH,
    },
    {
      provide: ACCOUNTS_TRANSACTIONS_JOURNEY_ARRANGEMENT_MANAGER_BASE_PATH,
      useExisting: APP_ARRANGEMENT_MANAGER_BASE_PATH,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: StepUpInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoanInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TransfersInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AccountsInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TransfersSaveBeneficiaryInterceptor,
      multi: true,
    },
    { provide: LOCALE_ID, useValue: 'en-GB' },
    {
      provide: AMOUNT_CONFIG_TOKEN,
      useValue: amountConfig,
    },
    {
      provide: ENVIRONMENT_CONFIG,
      useValue: environmentConfig,
    },
    {
      provide: DEV_TOOLS_PREVENTION_ENABLED,
      useValue: environment.disableDevTools ?? false,
    },
    provideHttpClient(withInterceptorsFromDi(), withXsrfConfiguration({})),
    DatePipe,
  ],
  bootstrap: [AppComponent],
})
export class AppModule { }
